/**
 * Font embedding for PNG export.
 *
 * `html-to-image` renders the slide inside an isolated SVG <foreignObject>,
 * which does NOT inherit the page's loaded @font-face web fonts. With
 * `skipFonts: true` the export therefore falls back to system fonts whose
 * metrics differ from the web fonts — most visibly with display serifs like
 * "Playfair Display", whose taller glyphs overflow and overlap the body text.
 *
 * This module fetches the Google Fonts @font-face rules actually used by the
 * captured slides (only the families/weights/styles in use), inlines the font
 * files as base64 data URIs, and returns a CSS string to pass as html-to-image's
 * `fontEmbedCSS`. Font files are cached so repeated exports don't re-download.
 *
 * Everything is best-effort: any failure resolves to an empty string so the
 * caller can fall back to the previous behaviour and never breaks the export.
 */

// Web font families loaded via <link> in index.html and used by the slide
// renderers (see getFontFamily in TwitterSlide/StorytellerSlide).
const WEB_FONTS = [
  'Inter',
  'Montserrat',
  'Playfair Display',
  'JetBrains Mono',
  'Lora',
  'Raleway',
  'Caveat',
];

export interface UsedFace {
  family: string;
  weight: string; // numeric string, e.g. "400" | "700"
  style: 'normal' | 'italic';
}

interface RawFace {
  block: string; // the @font-face { ... } text (url not yet inlined)
  weight: string; // single ("700") or range ("400 900")
  style: 'normal' | 'italic';
}

// family -> raw @font-face faces (latin/latin-ext subsets)
let rawByFamily: Map<string, RawFace[]> | null = null;
let rawPromise: Promise<Map<string, RawFace[]>> | null = null;
// gstatic url -> base64 data URI (font file cache)
const fileCache = new Map<string, Promise<string | null>>();

async function loadRawFaces(): Promise<Map<string, RawFace[]>> {
  if (rawByFamily) return rawByFamily;
  if (rawPromise) return rawPromise;

  rawPromise = (async () => {
    const map = new Map<string, RawFace[]>();
    const hrefs = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
      .map((l) => l.href)
      .filter((h) => h.includes('fonts.googleapis.com'));

    await Promise.all(
      hrefs.map(async (href) => {
        try {
          const res = await fetch(href);
          if (!res.ok) return;
          const css = await res.text();
          const blocks = css.match(/@font-face\s*{[^}]*}/g) || [];
          for (const block of blocks) {
            // Keep only latin + latin-ext to keep the embedded payload small.
            const ur = block.match(/unicode-range:\s*([^;}]+)/i)?.[1] || '';
            if (ur && !/U\+0000|U\+0100/i.test(ur)) continue;
            const fam = block.match(/font-family:\s*['"]([^'"]+)['"]/i)?.[1];
            if (!fam) continue;
            const weight = block.match(/font-weight:\s*([^;}]+)/i)?.[1]?.trim() || '400';
            const styleRaw = block.match(/font-style:\s*([^;}]+)/i)?.[1]?.trim() || 'normal';
            const style: 'normal' | 'italic' = styleRaw.startsWith('italic') ? 'italic' : 'normal';
            if (!map.has(fam)) map.set(fam, []);
            map.get(fam)!.push({ block, weight, style });
          }
        } catch {
          /* skip this stylesheet */
        }
      }),
    );

    rawByFamily = map;
    return map;
  })();

  return rawPromise;
}

async function fetchDataUri(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function fileToDataUri(url: string): Promise<string | null> {
  if (!fileCache.has(url)) fileCache.set(url, fetchDataUri(url));
  return fileCache.get(url)!;
}

async function inlineBlock(block: string): Promise<string> {
  const urlRe = /url\((https:\/\/fonts\.gstatic\.com\/[^)'"]+)\)/g;
  const urls = [...block.matchAll(urlRe)].map((m) => m[1]);
  const pairs = await Promise.all(urls.map(async (u) => [u, await fileToDataUri(u)] as const));
  let out = block;
  for (const [u, dataUri] of pairs) {
    if (dataUri) out = out.split(u).join(dataUri);
  }
  return out;
}

function faceMatches(raw: RawFace, used: UsedFace): boolean {
  if (raw.style !== used.style) return false;
  const uw = parseInt(used.weight, 10) || 400;
  const nums = raw.weight.split(/\s+/).map((n) => parseInt(n, 10)).filter((n) => !isNaN(n));
  if (nums.length >= 2) return uw >= nums[0] && uw <= nums[1]; // variable-font range
  if (nums.length === 1) return nums[0] === uw;
  return true;
}

/**
 * Scans the given element subtrees for the (family, weight, style) web font
 * combinations actually in use.
 */
export function collectUsedFontFaces(roots: HTMLElement[]): UsedFace[] {
  const seen = new Set<string>();
  const out: UsedFace[] = [];
  for (const root of roots) {
    const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
    for (const el of elements) {
      let cs: CSSStyleDeclaration;
      try {
        cs = window.getComputedStyle(el);
      } catch {
        continue;
      }
      const ff = cs.fontFamily || '';
      const family = WEB_FONTS.find((w) => ff.includes(w));
      if (!family) continue;
      const weight = String(cs.fontWeight || '400');
      const style: 'normal' | 'italic' = (cs.fontStyle || 'normal').startsWith('italic') ? 'italic' : 'normal';
      const key = family + '|' + weight + '|' + style;
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ family, weight, style });
      }
    }
  }
  return out;
}

/**
 * Returns inlined @font-face CSS (font files as base64) for the given faces,
 * suitable for html-to-image's `fontEmbedCSS`. Only the families/weights/styles
 * in use are embedded. Best-effort: returns '' on any failure.
 */
export async function getFontEmbedCSS(faces: UsedFace[]): Promise<string> {
  try {
    if (!faces.length) return '';
    const map = await loadRawFaces();

    const byFamily = new Map<string, UsedFace[]>();
    for (const f of faces) {
      if (!byFamily.has(f.family)) byFamily.set(f.family, []);
      byFamily.get(f.family)!.push(f);
    }

    const keptBlocks: string[] = [];
    for (const [family, used] of byFamily) {
      const familyFaces = map.get(family);
      if (!familyFaces || !familyFaces.length) continue;
      let matched = familyFaces.filter((raw) => used.some((u) => faceMatches(raw, u)));
      // Safety: never drop an entire used family due to a parse mismatch.
      if (matched.length === 0) matched = familyFaces;
      keptBlocks.push(...matched.map((m) => m.block));
    }

    const inlined = await Promise.all(keptBlocks.map(inlineBlock));
    return inlined.filter(Boolean).join('\n');
  } catch {
    return '';
  }
}
